import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, doc, updateDoc, getDoc, setDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase/firebase";
import { useAuth } from "../Context/AuthContext";
import { useWeddingData } from "../hooks/useWeddingData";
import ButtonComponent from "../ButtonComponent/ButtonComponent";
import {
  DEFAULT_THEME,
  createThemeStyle,
  sanitizeTheme,
} from "../utils/themeUtils";
import {
  RiCheckLine,
  RiUserHeartLine,
  RiCloseCircleLine,
  RiTimeLine,
  RiTeamLine,
  RiSearchLine,
} from "react-icons/ri";
import "./ChangeGuestState.scss";
import { syncGuestFamilyToPublicSite } from "../helpers/getSync";

const GROOM_COLLECTION = "GroomGuests";
const BRIDE_COLLECTION = "BrideGuests";

const ChangeGuestState = () => {
  const { user } = useAuth();
  const { weddingData, loading: weddingLoading } = useWeddingData();

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [updatedStatuses, setUpdatedStatuses] = useState({});
  const [savingKey, setSavingKey] = useState("");
  const [savedKey, setSavedKey] = useState("");

  const [siteTheme, setSiteTheme] = useState(null);
  const [themeLoading, setThemeLoading] = useState(true);

  const navigate = useNavigate();
  const handleGoBack = () => navigate("/dashboard");

  const bride_name = weddingData?.bride_name || "Bride";
  const groom_name = weddingData?.groom_name || "Groom";
  const siteSlug = weddingData?.slug || "";

  const groomCollectionName = GROOM_COLLECTION;
  const brideCollectionName = BRIDE_COLLECTION;

  const normalize = (value) => value.toLowerCase().replace(/\s+/g, "");

  const themeStyle = useMemo(
    () => createThemeStyle(siteTheme || DEFAULT_THEME),
    [siteTheme]
  );

  useEffect(() => {
    let isMounted = true;

    const loadTheme = async () => {
      try {
        setThemeLoading(true);

        const fallbackTheme = sanitizeTheme(weddingData?.theme || DEFAULT_THEME);

        if (!siteSlug) {
          if (isMounted) setSiteTheme(fallbackTheme);
          return;
        }

        const siteSnap = await getDoc(doc(db, "sites", siteSlug));

        if (!isMounted) return;

        if (siteSnap.exists()) {
          const siteData = siteSnap.data();
          setSiteTheme(
            sanitizeTheme(siteData?.theme || weddingData?.theme || DEFAULT_THEME)
          );
        } else {
          setSiteTheme(fallbackTheme);
        }
      } catch (error) {
        console.error("Error loading site theme:", error);

        if (isMounted) {
          setSiteTheme(sanitizeTheme(weddingData?.theme || DEFAULT_THEME));
        }
      } finally {
        if (isMounted) {
          setThemeLoading(false);
        }
      }
    };

    if (!weddingLoading) {
      loadTheme();
    }

    return () => {
      isMounted = false;
    };
  }, [weddingData, weddingLoading, siteSlug]);

  const getStatusIcon = (status) => {
    if (status === "Participation Confirmed") return <RiUserHeartLine />;
    if (status === "Not Participating") return <RiCloseCircleLine />;
    return <RiTimeLine />;
  };

  const handleSearch = async () => {
    if (!user) return;

    if (searchQuery.trim().length === 0) {
      setSearchResults([]);
      setHasSearched(false);
      return;
    }

    setLoading(true);
    setHasSearched(true);
    setSavedKey("");

    try {
      const groomGuestsRef = collection(db, "users", user.uid, groomCollectionName);
      const brideGuestsRef = collection(db, "users", user.uid, brideCollectionName);

      const [groomSnapshot, brideSnapshot] = await Promise.all([
        getDocs(groomGuestsRef),
        getDocs(brideGuestsRef),
      ]);

      const searchQueryLower = normalize(searchQuery);

      const processGuests = (docs, collectionName, sideLabel) => {
        return docs.flatMap((d) =>
          (d.data().guests || [])
            .map((guest, index) => {
              if (guest?.name) {
                return {
                  ...guest,
                  name: guest.name,
                  index,
                  documentId: d.id,
                  collectionName,
                  familyName: d.data().familyName || "Unknown",
                  guestType: d.data().guestType || "",
                  sideLabel,
                };
              }
              return null;
            })
            .filter(Boolean)
        );
      };

      const groomGuests = processGuests(
        groomSnapshot.docs,
        groomCollectionName,
        `${groom_name}'s Side`
      );

      const brideGuests = processGuests(
        brideSnapshot.docs,
        brideCollectionName,
        `${bride_name}'s Side`
      );

      const allGuests = [...groomGuests, ...brideGuests].filter(
        (g) => g.name && normalize(g.name).includes(searchQueryLower)
      );

      allGuests.sort((a, b) => a.name.localeCompare(b.name));
      setSearchResults(allGuests);
    } catch (err) {
      console.error("Error fetching guests:", err);
      alert("Failed to search guests. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (familyId, index, value) => {
    const key = `${familyId}-${index}`;
    setUpdatedStatuses((prev) => ({ ...prev, [key]: value }));
  };

  const handleChangeState = async (familyId, index, collectionName) => {
    if (!user) return;

    const key = `${familyId}-${index}`;
    const newStatus = updatedStatuses[key];

    if (!newStatus) {
      alert("Please select a new status.");
      return;
    }

    try {
      setSavingKey(key);
      setSavedKey("");

      const privateRef = doc(db, "users", user.uid, collectionName, familyId);
      const privateSnap = await getDoc(privateRef);
      const privateData = privateSnap.data();

      if (!privateData || !Array.isArray(privateData.guests)) {
        alert("Guest data not found.");
        return;
      }

      const updatedGuests = [...privateData.guests];
      updatedGuests[index] = { ...updatedGuests[index], status: newStatus };

      await updateDoc(privateRef, { guests: updatedGuests });

      await syncGuestFamilyToPublicSite({
        uid: user.uid,
        slug: siteSlug,
        collectionName,
        familyDocId: familyId,
      });

      if (siteSlug) {
        const publicRef = doc(db, "sites", siteSlug, collectionName, familyId);
        const publicSnap = await getDoc(publicRef);

        if (publicSnap.exists()) {
          await updateDoc(publicRef, { guests: updatedGuests });
        } else {
          await setDoc(publicRef, {
            familyName: privateData.familyName || "Unknown",
            guestType: privateData.guestType || "",
            guests: updatedGuests,
            createdBy: privateData.createdBy || user?.email || "unknown",
            createdAt: privateData.createdAt || null,
          });
        }
      }

      setUpdatedStatuses((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });

      setSearchResults((prev) =>
        prev.map((guest) =>
          guest.documentId === familyId &&
          guest.index === index &&
          guest.collectionName === collectionName
            ? { ...guest, status: newStatus }
            : guest
        )
      );

      setSavedKey(key);
      setTimeout(() => setSavedKey(""), 1800);
    } catch (err) {
      console.error("Error updating status:", err);
      alert("Failed to update status");
    } finally {
      setSavingKey("");
    }
  };

  if (!user || weddingLoading || themeLoading || !siteTheme) return null;

  const totalResults = searchResults.length;
  const confirmedCount = searchResults.filter(
    (guest) => guest.status === "Participation Confirmed"
  ).length;
  const pendingCount = searchResults.filter(
    (guest) => !guest.status || guest.status === "Pending"
  ).length;
  const notParticipatingCount = searchResults.filter(
    (guest) => guest.status === "Not Participating"
  ).length;

  return (
    <section className="chgState" style={themeStyle}>
      <div className="chgState__hero">
        <div>
          <p className="chgState__eyebrow">Guest Status Manager</p>
          <h1 className="chgState__title">Change Guest Status</h1>
        </div>
      </div>

      <div className="chgState__summaryGrid">
        <div className="chgState__summaryCard chgState__summaryCard--results">
          <span className="chgState__summaryIcon">
            <RiTeamLine />
          </span>
          <div>
            <p className="chgState__summaryLabel">Results</p>
            <h3 className="chgState__summaryValue">{totalResults}</h3>
          </div>
        </div>

        <div className="chgState__summaryCard">
          <span className="chgState__summaryIcon">
            <RiUserHeartLine />
          </span>
          <div>
            <p className="chgState__summaryLabel">Confirmed</p>
            <h3 className="chgState__summaryValue">{confirmedCount}</h3>
          </div>
        </div>

        <div className="chgState__summaryCard">
          <span className="chgState__summaryIcon">
            <RiTimeLine />
          </span>
          <div>
            <p className="chgState__summaryLabel">Pending</p>
            <h3 className="chgState__summaryValue">{pendingCount}</h3>
          </div>
        </div>

        <div className="chgState__summaryCard">
          <span className="chgState__summaryIcon">
            <RiCloseCircleLine />
          </span>
          <div>
            <p className="chgState__summaryLabel">Not Participating</p>
            <h3 className="chgState__summaryValue">{notParticipatingCount}</h3>
          </div>
        </div>
      </div>

      <div className="chgState__panel">
        <div className="chgState__search">
          <div className="chgState__inputWrap">
            <span className="chgState__inputIcon">
              <RiSearchLine />
            </span>
            <input
              type="text"
              className="chgState__input"
              placeholder="Search guest name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
          </div>

          <ButtonComponent
            as="button"
            className="chgState__searchBtn"
            onClick={handleSearch}
            disabled={loading}
          >
            <RiSearchLine />
            <span>{loading ? "Searching..." : "Search"}</span>
          </ButtonComponent>
        </div>

        {loading && <p className="chgState__loading">Searching…</p>}

        <div className="chgState__results">
          {hasSearched && !loading && searchResults.length === 0 && (
            <div className="chgState__empty">No matching guests found.</div>
          )}

          {searchResults.map((result, idx) => {
            const key = `${result.documentId}-${result.index}`;
            const isSaving = savingKey === key;
            const isSaved = savedKey === key;

            return (
              <article
                className="chgState__item"
                key={`${result.collectionName}-${result.documentId}-${idx}`}
              >
                <div className="chgState__itemHead">
                  <div className="chgState__identity">
                    <div className="chgState__avatar">
                      {(result.name || "G").trim().charAt(0).toUpperCase()}
                    </div>

                    <div className="chgState__identityMeta">
                      <p className="chgState__name">{result.name}</p>
                      <p className="chgState__metaLine">
                        <span className="chgState__label">Family:</span>{" "}
                        {result.familyName}
                      </p>
                      <p className="chgState__metaLine">
                        <span className="chgState__label">List:</span>{" "}
                        {result.sideLabel}
                      </p>
                    </div>
                  </div>

                  <div className="chgState__currentStatus">
                    <span
                      className={`chgState__badge ${
                        result.status === "Participation Confirmed"
                          ? "is-confirmed"
                          : result.status === "Not Participating"
                          ? "is-not-participating"
                          : "is-pending"
                      }`}
                    >
                      <span className="chgState__badgeIcon">
                        {getStatusIcon(result.status)}
                      </span>
                      {result.status || "Pending"}
                    </span>
                  </div>
                </div>

                <div className="chgState__statusRow">
                  <div className="chgState__controls">
                    <select
                      className="chgState__select"
                      value={updatedStatuses[key] || ""}
                      onChange={(e) =>
                        handleStatusChange(result.documentId, result.index, e.target.value)
                      }
                    >
                      <option value="">Select new status</option>
                      <option value="Pending">Pending</option>
                      <option value="Participation Confirmed">
                        Participation Confirmed
                      </option>
                      <option value="Not Participating">Not Participating</option>
                    </select>

                    <ButtonComponent
                      as="button"
                      className={`chgState__saveBtn ${isSaved ? "is-success" : ""}`}
                      onClick={() =>
                        handleChangeState(
                          result.documentId,
                          result.index,
                          result.collectionName
                        )
                      }
                      disabled={!updatedStatuses[key] || isSaving}
                    >
                      {isSaving ? (
                        "Saving..."
                      ) : isSaved ? (
                        <>
                          <RiCheckLine />
                          <span>Saved</span>
                        </>
                      ) : (
                        "Save Change"
                      )}
                    </ButtonComponent>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ChangeGuestState;